import { useState } from 'react'
import useCodeBlock, { KEYLESS_BLOCKS } from '../hooks/useCodeBlock'
import CodeBlock, { CodeBlockProps } from './CodeBlock'
import FolderTabs from './FolderTabs'
import Tab from '@mui/material/Tab'

interface Props extends Omit<CodeBlockProps, 'code'> {
  name?: string
  target?: string
  secret?: string
  /** The signed-in enrollment: no key, and only the blocks that work without one (app#379). */
  keyless?: boolean
  network?: NetworkRecord
}

const TABS: readonly CodeBlockType[] = ['install', 'windows', 'container', 'compose', 'env']

const LABELS: Record<CodeBlockType, string> = {
  install: 'Mac / Linux',
  windows: 'Windows',
  container: 'Docker Run',
  compose: 'Docker Compose',
  env: 'Environment Vars',
}

// ⚠⚠ `name`, `target`, `secret` and `network` are taken out of `props` here, so the spread into
// `CodeBlock` below does not put them on its `Box`. There they became DOM attributes: the key was
// in the page as `secret="…"`, and `ph-mask` masks text only, so a session recording kept it.
export default function CodeBlockInstall({ keyless = false, name, target, secret, network, ...props }: Props) {
  const [tab, setTab] = useState<number>(0)
  // ⚠ Without a key only the installer works: it runs `nobgp register`, which prints the sign-in
  // link on the terminal. A container or an env file has no terminal to print it to.
  const tabs = keyless ? KEYLESS_BLOCKS : TABS
  const index = tab < tabs.length ? tab : 0

  const { code, language, hidden } = useCodeBlock({
    name,
    target,
    secret,
    network,
    keyless,
    block: tabs[index],
  })

  return (
    <>
      <FolderTabs orientation="horizontal" value={index} onChange={(_, value) => setTab(value)}>
        Docker
        {tabs.map(block => (
          <Tab key={block} label={LABELS[block]} />
        ))}
      </FolderTabs>

      <CodeBlock
        color={props.color ?? 'secondary'}
        className={hidden ? 'ph-mask' : ''}
        disabled={!target || (!keyless && !secret)}
        firstTabActive={!index}
        language={language}
        code={code}
        {...props}
      />
    </>
  )
}
